import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import supabase from '../utils/supabaseUtils';
import { logUserUpload } from '../utils/supabaseUtils';
import NewDashboardEditor from './NewDashboardEditor';
import AdminDashboardView from './AdminDashboardView';
import './NewDashboard.css';
import { useToast } from '@chakra-ui/react';

const removeBgApiKey = import.meta.env.VITE_REMOVE_BG_API_KEY;

const NewDashboard = () => {
  // Editor state
  const [cesdk, setCesdk] = useState(null);
  
  // State variables
  const [user, setUser] = useState(null);
  const [sourceImages, setSourceImages] = useState([]);
  const [primaryImage, setPrimaryImage] = useState(null);
  const [uploadedImageURL, setUploadedImageURL] = useState(null);
  const [templates, setTemplates] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [selectedTemplate, setSelectedTemplate] = useState(null);
  const [formData, setFormData] = useState({});
  const [exportedImage, setExportedImage] = useState(null);
  const [placeholderFields, setPlaceholderFields] = useState([]);
  // State for CESDK placeholders
  const [placeholderData, setPlaceholderData] = useState([]);
  const [showTemplateBrowser, setShowTemplateBrowser] = useState(false);
  // State for saved custom images
  const [savedImages, setSavedImages] = useState([]);
  // State for saved export
  const [currentExport, setCurrentExport] = useState(null);
  // State for image gallery
  const [galleryVisible, setGalleryVisible] = useState(false);
  const [currentGalleryImage, setCurrentGalleryImage] = useState(null);
  const [currentGalleryIndex, setCurrentGalleryIndex] = useState(0);
  // Navigation state
  const [activeSection, setActiveSection] = useState('dashboard');
  // Add these state variables after the existing state declarations (around line 30)
  const [isGeneratingPreview, setIsGeneratingPreview] = useState(false);
  
  // Add this state variable for checking admin status
  const [isAdmin, setIsAdmin] = useState(false);
  
  // Add stats state
  const [stats, setStats] = useState({
    users: 0,
    events: 0,
    templates: 0,
    uploads: 0
  });
  
  // Initialize toast
  const toast = useToast();
  
  const navigate = useNavigate();

  // CESDK editor ready handler
  const handleEditorReady = (cesdkInstance) => {
    setCesdk(cesdkInstance);
  };

  // Monitor CESDK initialization
  useEffect(() => {
    if (cesdk) {
      console.log("CESDK initialized successfully");
    }
  }, [cesdk]);

  // Fetch user session and data
  useEffect(() => {
    const fetchSessionAndData = async () => {
      const { data, error } = await supabase.auth.getSession();
      if (error || !data.session) {
        navigate('/login');
        return;
      }
      setUser(data.session.user);

      // Check if user is admin - for development, always return true
      setIsAdmin(true);

      // First, get active uploads from the database
      const { data: uploads, error: uploadError } = await supabase
        .from('user_uploads')
        .select('file_url, is_primary')
        .eq('user_id', data.session.user.id)
        .eq('status', 'active');

      if (uploadError) {
        console.error('Error fetching user uploads:', uploadError);
        setError(uploadError.message);
      } else {
        // Get the active file URLs
        const activeFileUrls = uploads.map(upload => upload.file_url);
        
        // Find primary image
        const primary = uploads.find(upload => upload.is_primary);
        setPrimaryImage(primary ? primary.file_url : null);

        // Fetch background-removed images
        const { data: images, error: imgError } = await supabase.storage
          .from('playmakerpics-images')
          .list(`background_removed_images/${data.session.user.id}`, {
            limit: 100,
            offset: 0,
            sortBy: { column: 'name', order: 'asc' },
          });

        if (imgError) {
          console.error('Error fetching source images:', imgError);
          setError(imgError.message);
        } else {
          // Only include images that are in the active uploads
          const formattedImages = images
            .filter(file => {
              const fileUrl = supabase.storage
                .from('playmakerpics-images')
                .getPublicUrl(`background_removed_images/${data.session.user.id}/${file.name}`)
                .data.publicUrl;
              return file.name !== '.emptyFolderPlaceholder' && activeFileUrls.includes(fileUrl);
            })
            .map(file => ({
              name: file.name,
              url: supabase.storage
                .from('playmakerpics-images')
                .getPublicUrl(`background_removed_images/${data.session.user.id}/${file.name}`)
                .data.publicUrl,
            }));
          setSourceImages(formattedImages);

          // If no primary image was set from the database, use the first active image
          if (!primaryImage && formattedImages.length > 0) {
            setPrimaryImage(formattedImages[0].url);
          }
        }
      }

      // Fetch templates
      const { data: templateData, error: templateError } = await supabase
        .from('templates')
        .select('id, name, thumbnail_url, placeholder_data, scene_url, style_category, style_subcategory')
        .order('updated_at', { ascending: false });
      if (templateError) {
        console.error('Error fetching templates:', templateError);
        setError(templateError.message);
      } else {
        setTemplates(templateData || []);
      }
      
      // Fetch saved custom images 
      // Include is_saved = false to also show exports from approval process
      const { data: exportData, error: exportError } = await supabase
        .from('exports')
        .select('id, template_id, export_url, exported_at, is_saved')
        .eq('user_id', data.session.user.id)
        .order('exported_at', { ascending: false });
        
      if (exportError) {
        console.error('Error fetching saved exports:', exportError);
      } else {
        // Map export data to include template name if available
        const savedExports = exportData?.map(export_item => {
          const template = templateData?.find(t => t.id === export_item.template_id);
          return {
            ...export_item,
            template_name: template ? template.name : 'Custom Design'
          };
        }) || [];
        
        // Filter out any exports with invalid URLs
        const validExports = savedExports.filter(item => item.export_url && !item.export_url.startsWith('blob:'));
        console.log(`Found ${validExports.length} valid exports out of ${savedExports.length} total`);
        
        setSavedImages(validExports);
      }
      
      // Fetch stats for admin view
      if (isAdmin) {
        try {
          // Fetch user count
          const { count: userCount, error: userError } = await supabase
            .from('app_users')
            .select('*', { count: 'exact', head: true });
            
          // Fetch event count
          const { count: eventCount, error: eventError } = await supabase
            .from('events')
            .select('*', { count: 'exact', head: true });
            
          // Fetch upload count
          const { count: uploadCount, error: uploadError } = await supabase
            .from('user_uploads')
            .select('*', { count: 'exact', head: true });
            
          setStats({
            users: userError ? 0 : userCount || 0,
            events: eventError ? 0 : eventCount || 0,
            templates: templates.length,
            uploads: uploadError ? 0 : uploadCount || 0
          });
        } catch (statsError) {
          console.error('Error fetching stats:', statsError);
        }
      }
    };
    
    fetchSessionAndData();
  }, [navigate, primaryImage]);

  // Handle file upload with background removal
  const handleFileChange = async (event) => {
    if (!user) {
      setError('Please log in to upload an image');
      return;
    }

    setIsLoading(true);
    setError(null);
    const file = event.target.files[0];
    if (!file) {
      setIsLoading(false);
      return;
    }

    try {
      // Process with remove.bg API
      const formData = new FormData();
      formData.append('image_file', file);

      const response = await fetch('https://api.remove.bg/v1.0/removebg', {
        method: 'POST',
        headers: { 'X-API-Key': removeBgApiKey },
        body: formData,
      });

      if (!response.ok) throw new Error(`Remove.bg API error: ${await response.text()}`);

      // Create a blob from the response
      const blob = await response.blob();
      const processedFile = new File([blob], `processed-${Date.now()}.png`, { type: 'image/png' });

      // Upload to Supabase storage
      const filePath = `background_removed_images/${user.id}/${processedFile.name}`;
      const { error: uploadError } = await supabase.storage
        .from('playmakerpics-images')
        .upload(filePath, processedFile, { upsert: true });

      if (uploadError) throw uploadError;

      // Get public URL
      const { publicUrl } = supabase.storage
        .from('playmakerpics-images')
        .getPublicUrl(filePath).data;

      // Log upload and update state
      const isFirstImage = sourceImages.length === 0;
      await logUserUpload(user.id, publicUrl, isFirstImage);
      setUploadedImageURL(publicUrl);
      const newImage = { name: processedFile.name, url: publicUrl };
      setSourceImages([...sourceImages, newImage]);
      if (isFirstImage) setPrimaryImage(publicUrl);
    } catch (err) {
      setError(err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Delete an image
  const handleDeleteImage = async (imageName) => {
    try {
      console.log('=== Starting Image Deletion Process ===');
      console.log('1. Initial Parameters:', { imageName, userId: user?.id });
      
      // First, get the file path and URL
      const filePath = `background_removed_images/${user.id}/${imageName}`;
      const imageUrl = supabase.storage.from('playmakerpics-images').getPublicUrl(filePath).data.publicUrl;
      console.log('2. File Details:', { filePath, imageUrl });

      // Log current state before deletion
      console.log('3. Current State:', {
        sourceImagesCount: sourceImages.length,
        primaryImage,
        isPrimaryImage: primaryImage === imageUrl
      });

      // Check if the upload exists in the database first
      console.log('4. Checking if upload exists in database...');
      const { data: existingUpload, error: checkError } = await supabase
        .from('user_uploads')
        .select('*')
        .eq('user_id', user.id)
        .eq('file_url', imageUrl)
        .single();

      if (checkError) {
        console.error('❌ Error checking upload:', checkError);
        throw checkError;
      }

      if (!existingUpload) {
        console.error('❌ No matching upload found in database');
        throw new Error('No matching upload found in database');
      }

      console.log('5. Found existing upload:', existingUpload);

      // Update the status in the user_uploads table to 'deleted'
      console.log('6. Attempting database update...');
      const { data, error: dbError } = await supabase
        .from('user_uploads')
        .update({ status: 'deleted' })
        .eq('id', existingUpload.id)  // Use the exact ID we found
        .select();

      if (dbError) {
        console.error('❌ Database update failed:', dbError);
        throw dbError;
      }
      console.log('✅ Database update result:', data);

      // Update UI state by filtering out the deleted image
      console.log('7. Updating UI state...');
      const updatedImages = sourceImages.filter(img => img.name !== imageName);
      setSourceImages(updatedImages);
      console.log('Updated sourceImages count:', updatedImages.length);

      // If we deleted the primary image, update the primary image
      if (primaryImage === imageUrl) {
        console.log('8. Primary image was deleted, updating primary image...');
        // Refetch the active uploads to get the most up-to-date data
        const { data: activeUploads } = await supabase
          .from('user_uploads')
          .select('file_url, is_primary')
          .eq('user_id', user.id)
          .eq('status', 'active');
        
        console.log('Active uploads after deletion:', activeUploads);
        
        const newPrimary = activeUploads?.find(upload => upload.is_primary);
        if (newPrimary) {
          console.log('Setting new primary from database:', newPrimary.file_url);
          setPrimaryImage(newPrimary.file_url);
        } else if (updatedImages.length > 0) {
          console.log('No primary in DB, using first active image:', updatedImages[0].url);
          setPrimaryImage(updatedImages[0].url);
        } else {
          console.log('No images remaining, setting primary to null');
          setPrimaryImage(null);
        }
      }

      console.log('=== Image Deletion Process Completed Successfully ===');
    } catch (err) {
      console.error('❌ Error in handleDeleteImage:', err);
      setError(err.message);
    }
  };

  // Set primary image
  const handleSetPrimary = async (url) => {
    try {
      setPrimaryImage(url);
      await supabase
        .from('user_uploads')
        .update({ is_primary: false })
        .eq('user_id', user.id);
      await supabase
        .from('user_uploads')
        .update({ is_primary: true })
        .eq('user_id', user.id)
        .eq('file_url', url);
    } catch (err) {
      console.error('Error setting primary image:', err);
      setError(err.message);
    }
  };

  // Handle logout
  const handleLogout = async () => {
    await supabase.auth.signOut();
    navigate('/login');
  };

  // Handle template customization
  const handleCustomizeTemplate = async (template) => {
    if (!cesdk) {
      setError("Editor not initialized. Please refresh the page and try again.");
      return;
    }
    
    setSelectedTemplate(template);
    setIsLoading(true);
    
    try {
      // Load the template scene into the hidden editor to analyze it
      if (!template.scene_url) {
        throw new Error("Template has no scene URL");
      }
      
      // Fetch scene data
      const response = await fetch(template.scene_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
      }
      
      const sceneText = await response.text();
      
      // Load the scene - try different methods for compatibility
      let loadSuccess = false;
      
      // Method 1: Direct loadFromString
      if (!loadSuccess && typeof cesdk.loadFromString === 'function') {
        try {
          await cesdk.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with direct method:", err.message);
        }
      }
      
      // Method 2: Engine scene loadFromString
      if (!loadSuccess && cesdk.engine && cesdk.engine.scene && 
          typeof cesdk.engine.scene.loadFromString === 'function') {
        try {
          await cesdk.engine.scene.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with engine method:", err.message);
        }
      }
      
      if (!loadSuccess) {
        throw new Error("Could not load template for analysis. Please try again later.");
      }
      
      // Find all blocks in the scene
      const scene = cesdk.engine.scene.get();
      if (!scene) {
        throw new Error("No scene is loaded");
      }
      
      // Get all pages in the scene
      const pages = cesdk.engine.block.getChildren(scene);
      
      // Get all blocks from all pages including nested blocks
      const getAllBlocks = () => {
        const allBlocks = [];
        
        const processBlock = (blockId) => {
          allBlocks.push(blockId);
          
          try {
            // Try to get children if this block has any
            const children = cesdk.engine.block.getChildren(blockId);
            if (children && children.length > 0) {
              children.forEach(childId => processBlock(childId));
            }
          } catch (e) {
            // Ignore errors for blocks that don't have children
          }
        };
        
        pages.forEach(pageId => processBlock(pageId));
        return allBlocks;
      };
      
      const allBlocks = getAllBlocks();
      console.log(`Found ${allBlocks.length} total blocks in template`);
      
      // Find text blocks that appear to be placeholders
      const textBlocks = allBlocks.filter(id => {
        try {
          return cesdk.engine.block.getType(id) === "//ly.img.ubq/text";
        } catch (e) {
          return false;
        }
      });
      
      console.log(`Found ${textBlocks.length} text blocks in template`);
      
      // Identify placeholder fields by analyzing text content and block names
      const fields = [];
      const detectedFields = new Set(); // To avoid duplicates
      
      textBlocks.forEach(id => {
        try {
          const blockName = cesdk.engine.block.getName(id) || "";
          const text = cesdk.engine.block.getString(id, "text/text") || "";
          
          console.log(`Text block: "${blockName}", content: "${text}"`);
          
          // Check if this block appears to be a placeholder
          const isPlaceholder = 
            // All uppercase text is usually a placeholder
            (text === text.toUpperCase() && text.length > 0 && text.length < 30) ||
            // Or block names that look like field names
            (/TITLE|NAME|LAST|FIRST|JERSEY|NUMBER/i.test(blockName)) ||
            // Special case for title blocks
            (blockName === "TITLE" || blockName === "Title" || text === "TITLE");
          
          if (isPlaceholder) {
            // Determine field name and type based on block name and content
            let fieldName = "";
            
            // Try to determine a good field name
            if (/JERSEY|JERSEY_NUMBER|NUMBER/i.test(blockName) || /^[0-9]+$/.test(text) || text === "JERSEY NUMBER") {
              fieldName = "JERSEY NUMBER";
            } else if (/LAST|LASTNAME|LAST_NAME|SURNAME/i.test(blockName) || text === "LAST NAMER") {
              fieldName = "LAST NAMER";
            } else if (/FIRST|FIRSTNAME|FIRST_NAME/i.test(blockName)) {
              fieldName = "FIRST NAME";
            } else if (/TITLE/i.test(blockName) || text === "TITLE") {
              fieldName = "TITLE";
            } else if (/NAME/i.test(blockName) || text === "NAME") {
              fieldName = "NAME";
            } else {
              // Use the text content as field name if it's all uppercase
              fieldName = text === text.toUpperCase() ? text : blockName;
            }
            
            // Only add if we haven't seen this field before
            if (fieldName && !detectedFields.has(fieldName)) {
              fields.push({
                name: fieldName,
                type: 'text',
                originalText: text,
                blockName: blockName,
                blockId: id
              });
              detectedFields.add(fieldName);
            }
          }
        } catch (e) {
          console.warn(`Error analyzing text block:`, e);
        }
      });
      
      // If no text fields were found, add a default NAME field
      if (fields.length === 0 || !fields.some(f => f.name === "NAME" || f.name === "LAST NAMER")) {
        fields.push({
          name: "NAME",
          type: "text",
          originalText: "",
          blockName: "",
          blockId: null
        });
      }
      
      console.log("Detected form fields:", fields);
      
      // Add ATHLETE IMAGE as a special field
      fields.push({
        name: "ATHLETE IMAGE",
        type: "image",
        originalText: "",
        blockName: "ATHLETE",
        blockId: null
      });
      
      setPlaceholderFields(fields);
      
      // Initialize form data with empty values
      const initialFormData = {};
      fields.forEach(field => {
        if (field.type === 'text') {
          initialFormData[field.name] = '';
        }
      });
      
      setFormData(initialFormData);
      
      // Set modal title to template name
      // No need to update placeholder data since we're using the fields array directly
      
    } catch (err) {
      console.error("Error analyzing template:", err);
      setError("Error analyzing template: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Handle form input changes
  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({ ...prev, [name]: value }));
  };
  
  // Generate preview of the customized template
  const handleGeneratePreview = async () => {
    if (!cesdk || !selectedTemplate) {
      setError("Editor not ready or no template selected");
      return;
    }
    
    // Check if we have a primary image
    if (!primaryImage) {
      setError("Please select a primary image first.");
      return;
    }
    
    setIsGeneratingPreview(true);
    setError(null);
    setExportedImage(null);
    
    try {
      // Load the template scene into the hidden editor
      if (!selectedTemplate.scene_url) {
        throw new Error("Template has no scene URL");
      }
      
      // Fetch scene data
      const response = await fetch(selectedTemplate.scene_url);
      if (!response.ok) {
        throw new Error(`Failed to fetch scene: ${response.statusText}`);
      }
      
      const sceneText = await response.text();
      
      // Load the scene - try different methods for compatibility
      let loadSuccess = false;
      
      // Method 1: Direct loadFromString
      if (!loadSuccess && typeof cesdk.loadFromString === 'function') {
        try {
          await cesdk.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with direct method:", err.message);
        }
      }
      
      // Method 2: Engine scene loadFromString
      if (!loadSuccess && cesdk.engine && cesdk.engine.scene && 
          typeof cesdk.engine.scene.loadFromString === 'function') {
        try {
          await cesdk.engine.scene.loadFromString(sceneText);
          loadSuccess = true;
        } catch (err) {
          console.warn("Failed loading with engine method:", err.message);
        }
      }
      
      if (!loadSuccess) {
        throw new Error("Could not load template. Please try again later.");
      }
      
      // Create a method to apply customizations to the scene
      const applyCustomizations = async () => {
        console.log("Applying customizations with form data:", formData);
        
        if (!cesdk.engine || !cesdk.engine.scene) {
          throw new Error("Scene API is not available");
        }
        
        const scene = cesdk.engine.scene.get();
        if (!scene) {
          throw new Error("No scene is loaded");
        }
        
        try {
          // Get all pages in the scene
          const pages = cesdk.engine.block.getChildren(scene);
          
          // Get all blocks from all pages
          const allBlocks = [];
          for (const pageId of pages) {
            try {
              const pageBlocks = cesdk.engine.block.getChildren(pageId);
              allBlocks.push(...pageBlocks);
            } catch (e) {
              console.warn(`Could not get children of page ${pageId}:`, e);
            }
          }
          
          // Get grandchildren blocks since text might be inside deeper blocks
          const allBlocksWithChildren = [...allBlocks];
          
          // Add grandchildren and great-grandchildren to ensure we catch all text blocks
          for (const blockId of allBlocks) {
            try {
              if (cesdk.engine.block.getType(blockId) !== "//ly.img.ubq/text") {
                const childrenBlocks = cesdk.engine.block.getChildren(blockId);
                
                // Add immediate children
                allBlocksWithChildren.push(...childrenBlocks);
                
                // And try to get their children too (for deeply nested blocks)
                for (const childId of childrenBlocks) {
                  try {
                    const grandchildren = cesdk.engine.block.getChildren(childId);
                    allBlocksWithChildren.push(...grandchildren);
                  } catch (e) {
                    // Ignore errors on grandchildren
                  }
                }
              }
            } catch (e) {
              // Ignore errors on getting children
            }
          }
          
          console.log(`Found ${allBlocksWithChildren.length} total blocks (including nested blocks)`);
          
          // Log all text blocks before we try to modify them
          const textBlocks = allBlocksWithChildren.filter(id => {
            try {
              return cesdk.engine.block.getType(id) === "//ly.img.ubq/text";
            } catch (e) {
              return false;
            }
          });
          
          console.log(`Found ${textBlocks.length} text blocks to process:`);
          textBlocks.forEach(id => {
            try {
              const name = cesdk.engine.block.getName(id) || "unnamed";
              const text = cesdk.engine.block.getString(id, "text/text");
              console.log(`- Text block: "${name}", content: "${text}"`);
            } catch (e) {
              console.log(`- Error getting text block info: ${e.message}`);
            }
          });
          
          // Apply customizations to text blocks and placeholders
          for (const blockId of allBlocksWithChildren) {
            try {
              const blockType = cesdk.engine.block.getType(blockId);
              const blockName = cesdk.engine.block.getName(blockId);
              
              // Set text for any text blocks
              if (blockType === "//ly.img.ubq/text") {
                try {
                  console.log(`Processing text block: ${blockName || "unnamed"}, ID: ${blockId}`);
                  
                  // Get current text to see if we need to replace it
                  const currentText = cesdk.engine.block.getString(blockId, "text/text");
                  console.log(`Current text: "${currentText}"`);
                  
                  // Go through all the text fields and see if this block matches any of them
                  let handled = false;
                  
                  // First check if this block directly matches one of our detected fields
                  placeholderFields.forEach(field => {
                    if (field.type === 'text' && field.blockId === blockId && formData[field.name]) {
                      console.log(`Directly replacing text in known block ${field.name}: "${currentText}" with "${formData[field.name]}"`);
                      cesdk.engine.block.setString(blockId, "text/text", formData[field.name]);
                      handled = true;
                    }
                  });
                  
                  // If not directly matched, try to match by name/content patterns
                  if (!handled) {
                    // Try to match by content or block name
                    for (const [fieldName, value] of Object.entries(formData)) {
                      if (!value) continue; // Skip empty values
                      
                      // Check various text matching strategies
                      if (
                        // Exact field name match with block name
                        (blockName && blockName.toUpperCase() === fieldName) ||
                        // Exact field name match with text content
                        (currentText && currentText === fieldName) ||
                        // For JERSEY NUMBER, check if it's a number field or contains NUMBER
                        (fieldName === "JERSEY NUMBER" && (/^[0-9]+$/.test(currentText) || /NUMBER/i.test(blockName) || /JERSEY/i.test(blockName))) ||
                        // For LAST NAMER, check if it contains LAST or NAMER
                        (fieldName === "LAST NAMER" && (/LAST/i.test(blockName) || /NAMER/i.test(blockName) || currentText === "LAST NAMER")) ||
                        // General pattern: all uppercase content
                        (currentText === currentText.toUpperCase() && currentText.length > 0 && currentText.length < 30)
                      ) {
                        console.log(`Replacing text via pattern matching for ${fieldName}: "${currentText}" with "${value}"`);
                        cesdk.engine.block.setString(blockId, "text/text", value);
                        handled = true;
                        break;
                      }
                    }
                  }
                  
                  // If field wasn't handled by specific fields, try to use NAME as fallback
                  if (!handled && formData["NAME"]) {
                    if (
                      (blockName === "TITLE" || blockName === "Title" || currentText === "TITLE") ||
                      (currentText && currentText === currentText.toUpperCase() && currentText.length < 30 && !/[a-z]/.test(currentText))
                    ) {
                      console.log(`Fallback: replacing with NAME field value: "${formData["NAME"]}"`);
                      cesdk.engine.block.setString(blockId, "text/text", formData["NAME"]);
                    }
                  }
                } catch (textErr) {
                  console.warn(`Error processing text block ${blockName || 'unnamed'}:`, textErr);
                }
              }
              
              // Apply primary image to placeholder graphics
              if (blockType === "//ly.img.ubq/graphic") {
                // Look for various indicators that this is a placeholder block
                const isPlaceholder = blockName === "Person" || 
                                      blockName === "Athlete" || 
                                      blockName === "ATHLETE" ||
                                      blockName === "PLAYER" ||
                                      blockName === "Player" || 
                                      /athlete|player|person|placeholder/i.test(blockName);
                
                // Check for placeholder metadata as a fallback
                let hasPlaceholderMetadata = false;
                try {
                  const placeholderMeta = cesdk.engine.block.getMetadata(blockId, "isPlaceholder", "false");
                  hasPlaceholderMetadata = placeholderMeta === "true";
                } catch (e) {
                  // Ignore errors checking metadata
                }
                
                if (isPlaceholder || hasPlaceholderMetadata) {
                  console.log(`Found athlete/person placeholder: ${blockName}`);
                  if (primaryImage) {
                    // Try different methods to set the image
                    try {
                      // 1. Check for fill
                      const fillId = cesdk.engine.block.getFill(blockId);
                      if (fillId && cesdk.engine.block.isValid(fillId)) {
                        const fillType = cesdk.engine.block.getType(fillId);
                        
                        // If it's an image fill, set it
                        if (fillType === "//ly.img.ubq/fill/image") {
                          try {
                            cesdk.engine.block.setSourceSet(fillId, "fill/image/sourceSet", [
                              {
                                uri: primaryImage,
                                width: 1080,
                                height: 720,
                              },
                            ]);
                          } catch (e) {
                            // Try the older method
                            cesdk.engine.block.setString(fillId, "fill/image/imageFileURI", primaryImage);
                          }
                        } else {
                          // If it's not an image fill, create a new one
                          const newFillId = cesdk.engine.block.createFill("image");
                          try {
                            cesdk.engine.block.setSourceSet(newFillId, "fill/image/sourceSet", [
                              {
                                uri: primaryImage,
                                width: 1080,
                                height: 720,
                              },
                            ]);
                          } catch (e) {
                            cesdk.engine.block.setString(newFillId, "fill/image/imageFileURI", primaryImage);
                          }
                          cesdk.engine.block.setFill(blockId, newFillId);
                        }
                      }
                    } catch (e) {
                      console.warn("Error setting image via fill:", e);
                    }
                  }
                }
              }
            } catch (err) {
              console.warn(`Error processing block:`, err);
            }
          }
        } catch (err) {
          console.error("Error in applying customizations:", err);
          throw err;
        }
      };
      
      // Apply customizations
      await applyCustomizations();
      
      // Export the customized scene as an image
      const scene = cesdk.engine.scene.get();
      const blob = await cesdk.engine.block.export(scene, "image/png", { 
        dpi: 300,
        transparency: true
      });
      
      // Create object URL for preview (just for display)
      const localObjectUrl = URL.createObjectURL(blob);
      
      // Upload to Supabase storage for permanent storage
      const exportFileName = `export-${selectedTemplate.id}-${Date.now()}.png`;
      let supabaseUrl = null;
      
      try {
        // Upload the blob to Supabase
        const { data, error: uploadError } = await supabase.storage
          .from("playmakerpics-images")
          .upload(`exports/preview/${user.id}/${exportFileName}`, blob, { 
            contentType: "image/png",
            upsert: true
          });
        
        if (uploadError) {
          console.warn("Warning: Supabase upload failed, using local object URL:", uploadError);
        } else {
          // Get the public URL
          supabaseUrl = supabase.storage
            .from("playmakerpics-images")
            .getPublicUrl(`exports/preview/${user.id}/${exportFileName}`).data.publicUrl;
          
          console.log("Successfully uploaded preview to Supabase:", supabaseUrl);
        }
      } catch (uploadError) {
        console.warn("Error uploading to Supabase:", uploadError);
        // Continue with local URL if upload fails
      }
      
      // Set the exportedImage to the Supabase URL if available, otherwise use the local URL
      setExportedImage({
        localUrl: localObjectUrl,  // For display in the UI
        supabaseUrl: supabaseUrl,  // For saving to database
        fileName: exportFileName   // For download functionality
      });
      
    } catch (err) {
      console.error("Preview generation error:", err);
      setError("Preview generation failed: " + err.message);
    } finally {
      setIsGeneratingPreview(false);
    }
  };

  // Save the customized template as a design
  const handleSaveCustomized = async () => {
    if (!exportedImage || !selectedTemplate) {
      setError("No preview to save. Please generate a preview first.");
      return;
    }
    
    setIsLoading(true);
    
    try {
      let finalUrl = exportedImage.supabaseUrl;
      
      // If we don't have a Supabase URL already, upload the local blob
      if (!finalUrl && exportedImage.localUrl) {
        // Convert the local URL to a blob
        const response = await fetch(exportedImage.localUrl);
        const blob = await response.blob();
        
        // Upload to Supabase storage
        const exportFileName = exportedImage.fileName || `export-${selectedTemplate.id}-${Date.now()}.png`;
        
        const { error: uploadError } = await supabase.storage
          .from("playmakerpics-images")
          .upload(`exports/${user.id}/${exportFileName}`, blob, { 
            contentType: "image/png",
            upsert: true
          });
        
        if (uploadError) throw uploadError;
        
        // Get public URL
        finalUrl = supabase.storage
          .from("playmakerpics-images")
          .getPublicUrl(`exports/${user.id}/${exportFileName}`).data.publicUrl;
      }
      
      // If we still don't have a URL, something is wrong
      if (!finalUrl) {
        throw new Error("Failed to get a valid URL for the image");
      }
      
      // Log export in database as saved
      const { data: exportRecord, error: exportError } = await supabase.from("exports").insert({
        user_id: user.id,
        template_id: selectedTemplate.id,
        export_url: finalUrl,
        exported_at: new Date().toISOString(),
        is_saved: true
      }).select().single();
      
      if (exportError) throw exportError;
      
      // Add to saved images state
      const newSavedImage = {
        ...exportRecord,
        template_name: selectedTemplate.name
      };
      
      setSavedImages(prev => [newSavedImage, ...prev]);
      
      // Show success message and close modal
      setError(null);
      alert("Design saved successfully!");
      setSelectedTemplate(null);
      setExportedImage(null);
      
    } catch (err) {
      console.error("Error saving design:", err);
      setError("Failed to save design: " + err.message);
    } finally {
      setIsLoading(false);
    }
  };

  // Add this new function for deleting saved designs
  const handleDeleteDesign = async (designId, exportUrl) => {
    try {
      setIsLoading(true);
      
      // 1. First delete from exports table
      const { error: dbError } = await supabase
        .from('exports')
        .delete()
        .eq('id', designId);
      
      if (dbError) {
        throw new Error(`Failed to delete design from database: ${dbError.message}`);
      }
      
      // 2. Try to delete the file from storage (this might fail if path is unclear)
      try {
        // Parse the URL to get the path
        const urlObj = new URL(exportUrl);
        const pathMatch = urlObj.pathname.match(/\/object\/public\/playmakerpics-images\/(.+?)(\?|$)/);
        
        if (pathMatch && pathMatch[1]) {
          const storagePath = decodeURIComponent(pathMatch[1]);
          console.log("Attempting to delete file at path:", storagePath);
          
          // Delete from storage
          const { error: storageError } = await supabase.storage
            .from('playmakerpics-images')
            .remove([storagePath]);
          
          if (storageError) {
            console.warn("Could not delete file from storage:", storageError);
          }
        }
      } catch (storageError) {
        console.warn("Error parsing or deleting file from storage:", storageError);
        // Continue anyway as the database record is more important
      }
      
      // 3. Update the UI by filtering out the deleted design
      setSavedImages(prevImages => prevImages.filter(img => img.id !== designId));
      
      toast({
        title: "Design deleted",
        description: "The design has been removed from your account",
        status: "success",
        duration: 3000,
        isClosable: true,
      });
      
    } catch (err) {
      console.error('Error deleting design:', err);
      setError(err.message);
      
      toast({
        title: "Error deleting design",
        description: err.message,
        status: "error",
        duration: 5000,
        isClosable: true,
      });
    } finally {
      setIsLoading(false);
    }
  };

  if (!user) return null;

  return (
    <div className="new-dashboard-container">
      {/* Hidden Dashboard Editor for CESDK initialization */}
      <div style={{ position: 'absolute', opacity: 0, pointerEvents: 'none', height: '1px', overflow: 'hidden' }}>
        <NewDashboardEditor onEditorReady={handleEditorReady} />
      </div>

      {/* Sidebar Navigation */}
      <div className="sidebar">
        <div className="logo">
          <h2>PlayMaker Pics</h2>
        </div>
        
        <nav className="main-nav">
          <ul>
            <li className={activeSection === 'dashboard' ? 'active' : ''} onClick={() => setActiveSection('dashboard')}>
              <div className="nav-icon">
                <i className="fa fa-home"></i>
              </div>
              <span>Dashboard</span>
            </li>
            <li className={activeSection === 'images' ? 'active' : ''} onClick={() => setActiveSection('images')}>
              <div className="nav-icon">
                <i className="fa fa-image"></i>
              </div>
              <span>My Images</span>
            </li>
            <li className={activeSection === 'templates' ? 'active' : ''} onClick={() => setActiveSection('templates')}>
              <div className="nav-icon">
                <i className="fa fa-object-group"></i>
              </div>
              <span>Templates</span>
            </li>
            <li className={activeSection === 'editor' ? 'active' : ''}>
              <Link to="/editor" className="nav-link">
                <div className="nav-icon">
                  <i className="fa fa-edit"></i>
                </div>
                <span>Editor</span>
              </Link>
            </li>
            {/* Add Admin section if user is admin */}
            {isAdmin && (
              <li className={activeSection === 'admin' ? 'active' : ''} onClick={() => setActiveSection('admin')}>
                <div className="nav-icon">
                  <i className="fa fa-shield"></i>
                </div>
                <span>Admin</span>
              </li>
            )}
            <li className={activeSection === 'settings' ? 'active' : ''} onClick={() => setActiveSection('settings')}>
              <div className="nav-icon">
                <i className="fa fa-cog"></i>
              </div>
              <span>Settings</span>
            </li>
          </ul>
        </nav>
        
        <div className="sidebar-footer">
          <button className="logout-button" onClick={handleLogout}>
            <i className="fa fa-sign-out"></i>
            <span>Logout</span>
          </button>
        </div>
      </div>

      {/* Main Content Area */}
      <div className="main-content">
        <header className="content-header">
          <div className="header-title">
            {activeSection === 'dashboard' && <h1>Dashboard</h1>}
            {activeSection === 'images' && <h1>My Images</h1>}
            {activeSection === 'templates' && <h1>Templates</h1>}
            {activeSection === 'admin' && <h1>Admin Panel</h1>}
            {activeSection === 'settings' && <h1>Settings</h1>}
          </div>
          
          <div className="header-actions">
            <div className="user-profile">
              <span className="user-name">{user?.user_metadata?.given_name || 'User'}</span>
              <div className="avatar">
                {user?.user_metadata?.avatar_url ? (
                  <img src={user.user_metadata.avatar_url} alt="User avatar" />
                ) : (
                  <div className="avatar-placeholder">
                    {(user?.user_metadata?.given_name?.[0] || user?.email?.[0] || 'U').toUpperCase()}
                  </div>
                )}
              </div>
            </div>
          </div>
        </header>
        
        <div className="content-body">
          {/* Dashboard Section */}
          {activeSection === 'dashboard' && (
            <div className="dashboard-section">
              {/* Welcome banner */}
              <div className="welcome-banner">
                <div className="welcome-text">
                  <h2>Welcome back, {user?.user_metadata?.given_name || 'Creator'}</h2>
                  <p>Create stunning sports photos with our powerful AI tools</p>
                </div>
                <div className="banner-actions">
                  <button className="action-button primary" onClick={() => document.getElementById('file-upload').click()}>
                    Upload New Image
                  </button>
                  <Link to="/editor" className="action-button secondary">
                    Open Editor
                  </Link>
                </div>
              </div>
              
              {/* Stats cards */}
              <div className="stats-grid">
                <div className="stats-card">
                  <div className="stats-icon">
                    <i className="fa fa-image"></i>
                  </div>
                  <div className="stats-info">
                    <h3>Images</h3>
                    <p className="stats-value">{sourceImages.length}</p>
                  </div>
                </div>
                
                <div className="stats-card">
                  <div className="stats-icon">
                    <i className="fa fa-paint-brush"></i>
                  </div>
                  <div className="stats-info">
                    <h3>Designs</h3>
                    <p className="stats-value">{savedImages.length}</p>
                  </div>
                </div>
                
                <div className="stats-card">
                  <div className="stats-icon">
                    <i className="fa fa-object-group"></i>
                  </div>
                  <div className="stats-info">
                    <h3>Templates</h3>
                    <p className="stats-value">{templates.length}</p>
                  </div>
                </div>
              </div>
              
              {/* Stats Overview - Only show on dashboard view */}
              {activeSection === 'dashboard' && (
                <div className="admin-stats">
                  <div className="stat-card">
                    <Link to="/dashboard/users" className="stat-link">
                      <div className="stat-icon users-icon">
                        <i className="fas fa-users"></i>
                      </div>
                      <div className="stat-info">
                        <h3>USERS</h3>
                        <p className="stat-value">{stats.users}</p>
                      </div>
                    </Link>
                  </div>
                  
                  <div className="stat-card">
                    <div className="stat-icon events-icon">
                      <i className="fas fa-calendar"></i>
                    </div>
                    <div className="stat-info">
                      <h3>EVENTS</h3>
                      <p className="stat-value">{stats.events}</p>
                    </div>
                  </div>
                  
                  <div className="stat-card">
                    <div className="stat-icon templates-icon">
                      <i className="fas fa-file-image"></i>
                    </div>
                    <div className="stat-info">
                      <h3>TEMPLATES</h3>
                      <p className="stat-value">{stats.templates}</p>
                    </div>
                  </div>
                  
                  <div className="stat-card">
                    <div className="stat-icon uploads-icon">
                      <i className="fas fa-upload"></i>
                    </div>
                    <div className="stat-info">
                      <h3>UPLOADS</h3>
                      <p className="stat-value">{stats.uploads}</p>
                    </div>
                  </div>
                </div>
              )}
              
              {/* Recent Designs Section */}
              <section className="dashboard-section recent-designs-section">
                <div className="section-header">
                  <h2>Recent Designs</h2>
                  <button className="view-all-button" onClick={() => navigate('/designs')}>
                    View all
                  </button>
                </div>
                <div className="recent-designs-grid">
                  {savedImages.length > 0 ? (
                    savedImages.slice(0, 4).map((design) => (
                      <div className="recent-card" key={design.id} onClick={() => navigate(`/editor/${design.id}`)}>
                        <div className="card-image">
                          <img src={design.export_url} alt={design.template_name} />
                          <div className="card-info">
                            <h3>{design.template_name}</h3>
                            <p>{new Date(design.exported_at).toLocaleDateString()}</p>
                          </div>
                        </div>
                      </div>
                    ))
                  ) : (
                    <div className="empty-state">
                      <div className="empty-icon">🎨</div>
                      <h3>No designs yet</h3>
                      <p>Create your first design by using one of our templates</p>
                      <button className="action-button primary" onClick={() => navigate('/templates')}>
                        Create a design
                      </button>
                    </div>
                  )}
                </div>
              </section>
            </div>
          )}
          
          {/* Images Section */}
          {activeSection === 'images' && (
            <div className="images-section">
              <div className="section-header">
                <h2>My Designs</h2>
              </div>
              
              {savedImages.length > 0 ? (
                <div className="designs-grid">
                  {savedImages.map((image) => (
                    <div key={image.id} className="design-card">
                      <div className="design-image">
                        <img 
                          src={image.export_url} 
                          alt="Saved design" 
                          onClick={() => {
                            setCurrentGalleryImage(image);
                            setGalleryVisible(true);
                            setCurrentGalleryIndex(savedImages.findIndex(img => img.id === image.id));
                          }}
                        />
                        <div className="design-actions">
                          <a
                            href={image.export_url}
                            download={`design-${Date.now()}.png`}
                            className="download-action"
                          >
                            Download
                          </a>
                          <button
                            className="view-action"
                            onClick={() => {
                              setCurrentGalleryImage(image);
                              setGalleryVisible(true);
                              setCurrentGalleryIndex(savedImages.findIndex(img => img.id === image.id));
                            }}
                          >
                            View
                          </button>
                          <button
                            className="delete-action"
                            onClick={() => handleDeleteDesign(image.id, image.export_url)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <i className="fa fa-paint-brush"></i>
                  </div>
                  <h3>No Designs Yet</h3>
                  <p>Create your first design in the editor</p>
                  <Link to="/editor" className="action-button primary">
                    Go to Editor
                  </Link>
                </div>
              )}
              
              <div className="section-header">
                <h2>My Images</h2>
                <button className="upload-button" onClick={() => document.getElementById('file-upload').click()}>
                  <i className="fa fa-upload"></i>
                  <span>Upload New</span>
                </button>
              </div>
              
              {sourceImages.length > 0 ? (
                <div className="images-grid">
                  {sourceImages.map((image, index) => (
                    <div key={index} className="image-card">
                      <div className="image-wrapper">
                        <img 
                          src={image.url} 
                          alt="User uploaded" 
                          onClick={() => {
                            setCurrentGalleryImage({export_url: image.url, template_name: "User Image"});
                            setGalleryVisible(true);
                          }}
                        />
                        <div className="image-actions">
                          <button
                            className={`primary-action ${primaryImage === image.url ? 'active' : ''}`}
                            onClick={() => handleSetPrimary(image.url)}
                          >
                            {primaryImage === image.url ? 'Primary' : 'Set as Primary'}
                          </button>
                          <button
                            className="view-action"
                            onClick={() => {
                              setCurrentGalleryImage({export_url: image.url, template_name: "User Image"});
                              setGalleryVisible(true);
                            }}
                          >
                            View
                          </button>
                          <button
                            className="delete-action"
                            onClick={() => handleDeleteImage(image.name)}
                          >
                            Delete
                          </button>
                        </div>
                      </div>
                      {primaryImage === image.url && (
                        <div className="image-primary-indicator">
                          <span className="primary-badge">Primary</span>
                        </div>
                      )}
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <i className="fa fa-image"></i>
                  </div>
                  <h3>No Images Yet</h3>
                  <p>Upload your first image to get started</p>
                  <button className="action-button primary" onClick={() => document.getElementById('file-upload').click()}>
                    Upload Image
                  </button>
                </div>
              )}
            </div>
          )}
          
          {/* Templates Section */}
          {activeSection === 'templates' && (
            <div className="templates-section">
              <div className="section-header">
                <h2>Available Templates</h2>
                <Link to="/editor" className="action-button secondary">
                  Create New Template
                </Link>
              </div>
              
              {templates.length > 0 ? (
                <div className="templates-grid">
                  {templates.map((template) => (
                    <div key={template.id} className="template-card">
                      <div className="template-image">
                        <img src={template.thumbnail_url} alt={template.name} />
                        <div className="template-overlay">
                          <button 
                            className="use-template-button"
                            onClick={() => handleCustomizeTemplate(template)}
                          >
                            Customize
                          </button>
                        </div>
                      </div>
                      <div className="template-info">
                        <h3>{template.name}</h3>
                        <p>{template.style_category} • {template.style_subcategory || 'General'}</p>
                      </div>
                    </div>
                  ))}
                </div>
              ) : (
                <div className="empty-state">
                  <div className="empty-icon">
                    <i className="fa fa-object-group"></i>
                  </div>
                  <h3>No Templates Available</h3>
                  <p>Templates will appear here once created</p>
                  <Link to="/editor" className="action-button primary">
                    Create Template
                  </Link>
                </div>
              )}
            </div>
          )}
          
          {/* Admin Section - New */}
          {activeSection === 'admin' && isAdmin && (
            <AdminDashboardView />
          )}
          
          {/* Settings Section */}
          {activeSection === 'settings' && (
            <div className="settings-section">
              <div className="section-card">
                <h2>Account Settings</h2>
                
                <div className="setting-group">
                  <label>Name</label>
                  <div className="setting-value">
                    {user?.user_metadata?.given_name} {user?.user_metadata?.family_name}
                  </div>
                </div>
                
                <div className="setting-group">
                  <label>Email</label>
                  <div className="setting-value">{user?.email}</div>
                </div>
                
                <div className="setting-actions">
                  <button className="action-button secondary">
                    Edit Profile
                  </button>
                  <button className="action-button danger" onClick={handleLogout}>
                    Log Out
                  </button>
                </div>
              </div>
            </div>
          )}
        </div>
      </div>

      {/* Hidden File Input */}
      <input
        type="file"
        id="file-upload"
        accept="image/*"
        onChange={handleFileChange}
        style={{ display: 'none' }}
      />

      {/* Loading Overlay */}
      {isLoading && (
        <div className="loading-overlay">
          <div className="loading-spinner"></div>
          <p>Processing your request...</p>
        </div>
      )}

      {/* Error Toast */}
      {error && (
        <div className="error-toast">
          <p>{error}</p>
          <button onClick={() => setError(null)}>×</button>
        </div>
      )}

      {/* Image Gallery Modal */}
      {galleryVisible && currentGalleryImage && (
        <div className="gallery-modal">
          <div className="gallery-content">
            <button className="gallery-close" onClick={() => setGalleryVisible(false)}>×</button>
            
            <div className="gallery-image-container">
              <img src={currentGalleryImage.export_url} alt={currentGalleryImage.template_name} />
            </div>
            
            <div className="gallery-info">
              <h3>{currentGalleryImage.template_name}</h3>
              <p>Created on {new Date(currentGalleryImage.exported_at).toLocaleDateString()}</p>
            </div>
            
            {savedImages.length > 1 && (
              <div className="gallery-navigation">
                <button className="nav-button prev-button" onClick={() => {
                  const prevIndex = (currentGalleryIndex - 1 + savedImages.length) % savedImages.length;
                  setCurrentGalleryIndex(prevIndex);
                  setCurrentGalleryImage(savedImages[prevIndex]);
                }}>
                  ←
                </button>
                <div className="gallery-counter">
                  {currentGalleryIndex + 1} / {savedImages.length}
                </div>
                <button className="nav-button next-button" onClick={() => {
                  const nextIndex = (currentGalleryIndex + 1) % savedImages.length;
                  setCurrentGalleryIndex(nextIndex);
                  setCurrentGalleryImage(savedImages[nextIndex]);
                }}>
                  →
                </button>
              </div>
            )}
            
            <a 
              href={currentGalleryImage.export_url} 
              download={`${currentGalleryImage.template_name.replace(/\s+/g, '-')}-${Date.now()}.png`}
              className="gallery-download-button"
            >
              Download Image
            </a>
          </div>
        </div>
      )}

      {/* Template Customization Modal */}
      {selectedTemplate && (
        <div className="customization-modal">
          <div className="modal-content">
            <div className="modal-header">
              <h2>CUSTOMIZE {selectedTemplate?.name.toUpperCase()}</h2>
              <button 
                className="close-button"
                onClick={() => setSelectedTemplate(null)}
              >×</button>
            </div>
            
            <div className="modal-body">
              <div className="customization-options">
                <h3>CUSTOMIZATION OPTIONS</h3>
                
                {/* Render text fields dynamically */}
                {placeholderFields.filter(field => field.type === 'text').map((field) => (
                  <div className="form-group" key={field.name}>
                    <label>{field.name}</label>
                    <p className="field-description">Enter the {field.name.toLowerCase()} to display</p>
                    <input
                      type="text"
                      name={field.name}
                      value={formData[field.name] || ''}
                      onChange={handleInputChange}
                      placeholder={`Enter ${field.name}`}
                      className="form-input"
                    />
                  </div>
                ))}
                
                {/* Always show athlete image section */}
                <div className="form-group">
                  <label>ATHLETE IMAGE</label>
                  <div className="athlete-preview">
                    {primaryImage ? (
                      <div className="image-preview">
                        <img src={primaryImage} alt="Primary athlete" className="preview-thumbnail" />
                        <div className="image-info">
                          <div>USING PRIMARY IMAGE</div>
                          <div className="image-hint">YOU CAN CHANGE THIS IN THE IMAGE GALLERY SECTION</div>
                        </div>
                      </div>
                    ) : (
                      <div className="no-image-warning">
                        <p>Please select a primary image first</p>
                      </div>
                    )}
                  </div>
                </div>
                
                <button 
                  className="generate-preview-button"
                  onClick={handleGeneratePreview}
                  disabled={!primaryImage}
                >
                  Generate Preview
                </button>
              </div>
              
              <div className="preview-section">
                <h3>PREVIEW</h3>
                <div className="preview-container">
                  {isGeneratingPreview ? (
                    <div className="preview-loading">
                      <div className="loading-spinner-small"></div>
                      <p>Generating preview...</p>
                    </div>
                  ) : exportedImage ? (
                    <img 
                      src={exportedImage.localUrl} 
                      alt="Customized Template Preview" 
                      className="preview-image"
                    />
                  ) : (
                    <div className="preview-placeholder">
                      <p>CLICK "GENERATE PREVIEW" TO SEE YOUR CUSTOMIZED TEMPLATE</p>
                      <img 
                        src={selectedTemplate?.thumbnail_url} 
                        alt="Template thumbnail" 
                        className="template-thumbnail-preview"
                      />
                    </div>
                  )}
                </div>
                {exportedImage && (
                  <div className="preview-actions">
                    <button 
                      className="download-preview-button"
                      onClick={() => {
                        const link = document.createElement("a");
                        link.href = exportedImage.localUrl;
                        link.download = `${selectedTemplate.name.replace(/\s+/g, '-')}-${Date.now()}.png`;
                        link.click();
                      }}
                    >
                      Download
                    </button>
                    <button 
                      className="save-preview-button"
                      onClick={handleSaveCustomized}
                    >
                      Save to My Designs
                    </button>
                  </div>
                )}
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NewDashboard; 